/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Pipeline Estimates Hook
 *
 * Fetches estimates that represent opportunities/deals in the sales pipeline.
 * Uses React Query for caching and state management.
 */

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import {
  DEFAULT_PAGE_SIZE,
  QUERY_GC_TIME,
  QUERY_STALE_TIME,
} from "@/lib/constants/query-config";
import { logger } from "@/lib/utils/logger";

import type { ClientStage } from "@/types/client-unified";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface PipelineProposal {
  id: string;
  status: string | null;
  sentDate: string | null;
  acceptedDate: string | null;
  rejectedDate: string | null;
}

export interface PipelineOpportunity {
  id: string;
  estimateNumber: string | null;
  name: string;
  stage: ClientStage;
  value: number;
  estimateStatus: string;
  dueDate: string | null;
  expiresAt: string | null;
  validUntil: string | null;
  clientId: string | null;
  clientName: string;
  clientCompany: string | null;
  projectId: string | null;
  projectName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  status: string;
  latestProposal: PipelineProposal | null;
}

export interface UsePipelineEstimatesParams {
  // Pagination
  page?: number;
  limit?: number;

  // Filtering
  search?: string;
  stage?: ClientStage | "all";
  clientId?: string;
  minValue?: number;
  maxValue?: number;
  months?: number;

  // Related data inclusion
  includeClient?: boolean;
  includeProject?: boolean;
}

// The API returns SuccessResponse<PipelineOpportunity[]> with pagination
// So we don't need a separate PipelineEstimatesResponse type

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook to fetch pipeline estimates (opportunities/deals)
 */
export function usePipelineEstimates(params: UsePipelineEstimatesParams = {}) {
  // Log hook invocation immediately
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    logger.debug("usePipelineEstimates hook invoked", { params });
  }

  const { getToken } = useAuthSafe();
  const { organizationId, isOrganizationReady, queryEnabled } =
    useOrganizationQueryGuard({
      enabled: true,
      debugKey: "usePipelineEstimates",
    });
  const queryClient = useQueryClient();

  // Log organization state immediately after getting it
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    logger.debug("usePipelineEstimates organization state", {
      organizationId,
      isOrganizationReady,
      hasGetToken: !!getToken,
    });
  }

  const {
    page = 1,
    limit = DEFAULT_PAGE_SIZE,
    search,
    stage = "all",
    clientId,
    minValue,
    maxValue,
    months,
    includeClient = true,
    includeProject = true,
  } = params;

  // Memoize API client
  const apiClient = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
      }),
    [getToken, organizationId],
  );

  // Build query key for React Query
  const queryKey = useMemo(
    () => [
      "pipeline-estimates",
      organizationId,
      page,
      limit,
      search,
      stage,
      clientId,
      minValue,
      maxValue,
      months,
      includeClient,
      includeProject,
    ],
    [
      organizationId,
      page,
      limit,
      search,
      stage,
      clientId,
      minValue,
      maxValue,
      months,
      includeClient,
      includeProject,
    ],
  );

  // Debug: Log hook state
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    logger.debug("usePipelineEstimates hook state", {
      organizationId,
      isOrganizationReady,
      enabled: queryEnabled,
      params: {
        page,
        limit,
        stage,
        search,
        clientId,
        minValue,
        maxValue,
        months,
      },
    });
  }

  // Fetch pipeline estimates
  const { data, isLoading, error, refetch, isFetching } = useQuery<{
    items: PipelineOpportunity[];
    pagination?: import("@/lib/utils/responses").PaginationInfo;
  }>({
    queryKey,
    queryFn: async () => {
      if (!organizationId || !isOrganizationReady) {
        logger.warn(
          "Pipeline estimates query disabled - organization not ready",
          {
            organizationId,
            isOrganizationReady,
          },
        );
        throw new Error("Organization not found or not ready");
      }

      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        stage: stage,
        includeClient: includeClient.toString(),
        includeProject: includeProject.toString(),
      });

      if (search) {
        queryParams.append("query", search);
      }

      if (clientId) {
        queryParams.append("clientId", clientId);
      }

      if (minValue !== undefined) {
        queryParams.append("minValue", minValue.toString());
      }

      if (maxValue !== undefined) {
        queryParams.append("maxValue", maxValue.toString());
      }

      if (months !== undefined) {
        queryParams.append("months", months.toString());
      }

      const url = `/api/pipeline/estimates?${queryParams.toString()}`;

      logger.debug("Fetching pipeline estimates", {
        url,
        organizationId,
        params: { page, limit, stage, search, clientId, minValue, maxValue },
      });

      const response = await apiClient.get<PipelineOpportunity[]>(url);

      if (isApiFailure(response)) {
        throw new Error(
          response.message || "Failed to fetch pipeline estimates",
        );
      }

      if (!response.data) {
        throw new Error("Invalid response from server");
      }

      return {
        items: response.data,
        pagination: response.pagination,
      };
    },
    enabled: queryEnabled,
    staleTime: QUERY_STALE_TIME.CRITICAL, // pipeline-estimates are CRITICAL (frequently changing)
    gcTime: QUERY_GC_TIME.STANDARD,
  });

  // Debug: Log query state
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    logger.debug("usePipelineEstimates query state", {
      isEnabled: queryEnabled,
      isLoading,
      isFetching,
      hasData: !!data,
      dataLength: data?.items?.length ?? 0,
      error: error ? String(error) : undefined,
    });
  }

  // Invalidate and refetch
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["pipeline-estimates"] });
  }, [queryClient]);

  return {
    opportunities: data?.items ?? [],
    pagination: data?.pagination,
    isLoading,
    isFetching,
    error,
    refetch,
    invalidate,
  };
}

/**
 * Hook to get pipeline estimates by stage
 */
export function usePipelineEstimatesByStage(stage: ClientStage | "all") {
  return usePipelineEstimates({ stage });
}

/**
 * Hook to get pipeline estimates for a specific client
 */
export function usePipelineEstimatesByClient(clientId: string) {
  return usePipelineEstimates({ clientId });
}
